import React from 'react';
import { IonApp, IonRouterOutlet } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Route, Redirect, useHistory, Switch } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { StatusBar, Style } from '@capacitor/status-bar';
import ChatList from './pages/ChatList';
import ChatSession from './pages/ChatSession';
import SignIn from './pages/SignIn';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Subscribe from './pages/Subscribe';
import Profile from './pages/Profile';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { lightTheme, darkTheme } from './theme/ThemeProvider';
import { useSafeAreaFallback } from './hooks/useSafeAreaFallback';
import { useMarketingCampaign } from './hooks/useMarketingCampaign';
import { CampaignModal } from './components/Marketing/CampaignModal';

// Ionic is already initialized in main.tsx, so we don't need to do it here

const DeepLinkHandler: React.FC = () => {
  const history = useHistory();

  React.useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const listener = CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
      try {
        const parsed = new URL(url);
        let routePath = parsed.pathname;

        if (!routePath || routePath === '/') {
          routePath = parsed.host ? `/${parsed.host}` : '/';
        }

        if (routePath === '/subscription-success') {
          const params = new URLSearchParams(parsed.search);
          params.set('checkout', 'success');
          history.replace(`/subscribe?${params.toString()}`);
          await Browser.close();
          return;
        } else if (routePath === '/subscription-cancel') {
          history.replace('/subscribe?checkout=cancel');
          await Browser.close();
          return;
        } else if (routePath === '/servicetitan') {
          const params = new URLSearchParams(parsed.search);
          const returnPath = params.get('return_path');
          const safeReturnPath = returnPath?.startsWith('/')
            && !returnPath.startsWith('//')
            && !returnPath.includes('\\')
            && !/[\r\n\0]/.test(returnPath)
            ? returnPath
            : '/chat/new';
          const status = params.get('status') || 'connected';
          history.replace(`${safeReturnPath}?servicetitan=${encodeURIComponent(status)}`);
          await Browser.close();
          return;
        }

        const target = `${routePath}${parsed.search || ''}${parsed.hash || ''}`;
        history.replace(target);
        await Browser.close();
      } catch (error) {
        console.error('[App] Failed to handle deep link:', error);
      }
    });

    return () => {
      listener.remove();
    };
  }, [history]);

  return null;
};

const AppRoutes: React.FC<{ isDarkMode: boolean; setIsDarkMode: (mode: boolean) => void }> = ({
  isDarkMode,
  setIsDarkMode
}) => {
  const { isInitialized, isAuthenticated, user } = useAuth();
  const { campaign } = useMarketingCampaign({ enabled: isAuthenticated });
  const [showModal, setShowModal] = React.useState(false);

  // Show modal for modal-type campaigns on every login (unless dismissed)
  React.useEffect(() => {
    if (
      isAuthenticated &&
      user &&
      campaign &&
      campaign.communication_type === 'modal'
    ) {
      // Check if this specific user has dismissed this campaign (do not show again)
      // Include user ID in the key so each user has their own dismissal preferences
      const dismissedKey = `campaign_dismissed_${user.id}_${campaign.id}`;
      const isDismissed = localStorage.getItem(dismissedKey) === 'true';

      if (!isDismissed) {
        setShowModal(true);
      } else {
        setShowModal(false);
      }
    } else if (!campaign || campaign.communication_type !== 'modal') {
      setShowModal(false);
    }
  }, [campaign, isAuthenticated, user]);

  // Handle modal close (X button or click outside) - show again next login
  const handleClose = React.useCallback(() => {
    setShowModal(false);
    // Don't set dismissed flag - modal will show again on next login
  }, []);

  // Handle "Do not show again" - never show again for this campaign for this user
  const handleDoNotShowAgain = React.useCallback(() => {
    if (campaign && user) {
      // Include user ID in the key so each user has their own dismissal preferences
      const dismissedKey = `campaign_dismissed_${user.id}_${campaign.id}`;
      localStorage.setItem(dismissedKey, 'true');
      setShowModal(false);
    }
  }, [campaign, user]);

  // IonReactRouter is always mounted so it owns the URL from the very first render.
  // ProtectedRoute handles the auth-initializing loading state for authenticated routes.
  // Public auth routes (signin, forgot-password, reset-password) are declared first and
  // are never wrapped in ProtectedRoute, so they render immediately on cold load.
  return (
    <>
      <IonReactRouter>
        <DeepLinkHandler />
        <IonRouterOutlet>
          <Switch>
            <Route exact path="/signin" component={SignIn} />
            <Route exact path="/forgot-password" component={ForgotPassword} />
            <Route exact path="/reset-password" component={ResetPassword} />
            <Route
              exact
              path="/"
              render={() => (
                <ProtectedRoute>
                  <ChatList isDarkMode={isDarkMode} onThemeChange={setIsDarkMode} />
                </ProtectedRoute>
              )}
            />
            <Route
              exact
              path="/chat/new"
              render={() => (
                <ProtectedRoute>
                  <ChatSession />
                </ProtectedRoute>
              )}
            />
            <Route
              exact
              path="/chat/:id"
              render={() => (
                <ProtectedRoute>
                  <ChatSession />
                </ProtectedRoute>
              )}
            />
            <Route
              exact
              path="/subscribe"
              render={() => (
                <ProtectedRoute>
                  <Subscribe />
                </ProtectedRoute>
              )}
            />
            <Route
              exact
              path="/subscription-success"
              render={({ location }) => {
                const params = new URLSearchParams(location.search);
                params.set('checkout', 'success');
                return <Redirect to={`/subscribe?${params.toString()}`} />;
              }}
            />
            <Route
              exact
              path="/subscription-cancel"
              render={() => <Redirect to="/subscribe?checkout=cancel" />}
            />
            <Route
              exact
              path="/profile"
              render={() => (
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              )}
            />
            <Route path="*">
              <Redirect to="/signin" />
            </Route>
          </Switch>
        </IonRouterOutlet>
      </IonReactRouter>
      {showModal && campaign && campaign.communication_type === 'modal' && (
        <CampaignModal
          campaign={campaign}
          onClose={handleClose}
          onDoNotShowAgain={handleDoNotShowAgain}
        />
      )}
    </>
  );
};

const App: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = React.useState(false);
  const { resumeTick, applySafeAreaFix: safeAreaFix } = useSafeAreaFallback({ enableLogging: true });

  React.useEffect(() => {
    document.body.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  React.useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const platform = Capacitor.getPlatform();

    const configureStatusBar = async () => {
      try {
        if (platform === 'android') {
          await StatusBar.setOverlaysWebView({ overlay: false });
          await StatusBar.setBackgroundColor({ color: '#ffffff' });
        }

        await StatusBar.setStyle({ style: Style.Light });
      } catch (error: any) {
        if (error?.code === 'UNIMPLEMENTED') {
          console.info('[App] StatusBar API not implemented on this platform. Skipping.');
          return;
        }

        console.error('[App] Failed to configure status bar:', error);
      }
    };

    configureStatusBar();
  }, []);

  React.useEffect(() => {
    document.documentElement.style.removeProperty('--keyboard-height');
    document.documentElement.style.removeProperty('--offset-bottom');
    document.documentElement.style.removeProperty('--keyboard-offset');
    document.documentElement.style.setProperty('--keyboard-height', '0px');
    document.documentElement.style.setProperty('--keyboard-transition', 'height 0.1s ease-out');

    const clearKeyboardState = () => {
      console.log('[App] Clearing global keyboard state');
      document.documentElement.style.removeProperty('--keyboard-height');
      document.documentElement.style.removeProperty('--offset-bottom');
      document.documentElement.style.removeProperty('--keyboard-offset');
    };

    const handleAppStateChange = () => {
      console.log('[App] App state changed, clearing keyboard state');
      clearKeyboardState();
      safeAreaFix('app-state-change');
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[App] App became visible, clearing keyboard state');
        clearKeyboardState();
        safeAreaFix('visibility-visible');
      } else if (document.visibilityState === 'hidden') {
        console.log('[App] App became hidden, clearing keyboard state');
        clearKeyboardState();
      }
    };

    const handlePageShow = () => {
      console.log('[App] Page shown, clearing keyboard state');
      clearKeyboardState();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleAppStateChange);
    safeAreaFix('initial-mount');

    if (Capacitor.isNativePlatform()) {
      const removeListener = CapacitorApp.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
        if (isActive) {
          console.log('[App] Native app became active, clearing keyboard state');
          clearKeyboardState();
          safeAreaFix('native-app-active');
        } else {
          console.log('[App] Native app went to background, clearing keyboard state');
          clearKeyboardState();
        }
      });

      return () => {
        removeListener.remove();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('pageshow', handlePageShow);
        window.removeEventListener('focus', handleAppStateChange);
      };
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleAppStateChange);
    };
  }, [safeAreaFix]);

  React.useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const handleAppStateChange = () => {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refresh_token');

      if (token && refreshToken) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Math.floor(Date.now() / 1000);

          if (payload.exp && payload.exp < currentTime + 300) {
            console.log('Token expiring soon, refreshing...');
            import('./services/authApi').then(({ authApi }) => {
              authApi.refresh().catch(error => {
                console.error('Failed to refresh token on app resume:', error);
              });
            });
          }
        } catch (error) {
          console.error('Error checking token expiration:', error);
        }
      }

      const ionApp = document.querySelector('ion-app');
      if (ionApp) {
        const currentTransform = ionApp.style.transform;
        ionApp.style.transform = currentTransform === 'translateZ(0)' ? 'translateZ(0.1px)' : 'translateZ(0)';
        requestAnimationFrame(() => {
          ionApp.style.transform = currentTransform;
        });
      }
    };

    document.addEventListener('visibilitychange', handleAppStateChange);
    window.addEventListener('focus', handleAppStateChange);
    window.addEventListener('pageshow', handleAppStateChange);
    window.addEventListener('pagehide', () => {
      console.log('App going to background');
    });

    return () => {
      document.removeEventListener('visibilitychange', handleAppStateChange);
      window.removeEventListener('focus', handleAppStateChange);
      window.removeEventListener('pageshow', handleAppStateChange);
      window.removeEventListener('pagehide', () => {});
    };
  }, []);

  React.useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refresh_token');

      if (token && refreshToken) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Math.floor(Date.now() / 1000);

          if (payload.exp && payload.exp < currentTime + 600) {
            console.log('Periodic token refresh...');
            import('./services/authApi').then(({ authApi }) => {
              authApi.refresh().catch(error => {
                console.error('Failed to refresh token periodically:', error);
              });
            });
          }
        } catch (error) {
          console.error('Error checking token expiration for periodic refresh:', error);
        }
      }
    }, 25 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <IonApp data-resume-tick={resumeTick}>
      <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
        <AuthProvider>
          <AppRoutes isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
        </AuthProvider>
      </ThemeProvider>
    </IonApp>
  );
};

export default App;
